'use client';

import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

const cardStyle = {
  background: '#FFFFFF',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  border: '1px solid #F0F0F0',
};

export default function Row5FinancialPulse() {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <CashRunway />
      <ARAgingSummary />
      <BurnVsRevenue />
    </section>
  );
}

// ── 1. Cash Runway ─────────────────────────────────────────────────────────

const RUNWAY = {
  months: 0,
  burnPerMonth: 0,
  cashOnHand: 0,
  history: [] as number[],
};

function runwayColor(months: number): { color: string; bg: string } {
  if (months > 12) return { color: '#047857', bg: 'rgba(16,185,129,0.10)' };
  if (months >= 6) return { color: '#92400E', bg: 'rgba(245,158,11,0.10)' };
  return                { color: '#B91C1C', bg: 'rgba(239,68,68,0.10)' };
}

function CashRunway() {
  const tone = runwayColor(RUNWAY.months);
  const w = 220;
  const h = 36;
  // Guard: empty history would give Math.max(...[]) = -Infinity.
  const max = RUNWAY.history.length ? Math.max(...RUNWAY.history) : 0;
  const min = RUNWAY.history.length ? Math.min(...RUNWAY.history) : 0;
  const range = max - min || 1;
  const xFor = (i: number) => (i / (RUNWAY.history.length - 1)) * w;
  const yFor = (v: number) => h - ((v - min) / range) * (h - 4) - 2;
  const path = RUNWAY.history.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(v)}`).join(' ');

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-center gap-2 mb-1">
        <Icon name="BanknotesIcon" size={15} style={{ color: '#10B981' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          Cash Runway
        </h3>
      </div>
      <p className="text-xs text-gray-400 mb-3">At current burn rate</p>
      <div className="flex items-baseline gap-2 mb-2">
        <p
          className="text-4xl font-bold leading-none"
          style={{ color: tone.color, fontFamily: 'Inter, Poppins, sans-serif' }}
        >
          {RUNWAY.months.toFixed(1)}
        </p>
        <span className="text-sm font-semibold" style={{ color: tone.color }}>months</span>
      </div>
      <p className="text-[11px] text-gray-500 mb-3">
        Burn ${(RUNWAY.burnPerMonth / 1000).toFixed(0)}k/mo · Cash ${(RUNWAY.cashOnHand / 1_000_000).toFixed(2)}M
      </p>
      <div className="pt-2 border-t border-gray-100">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1">6-month runway trend</p>
        <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block">
          <path d={path} stroke={tone.color} strokeWidth="1.75" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}

// ── 2. AR Aging Summary ────────────────────────────────────────────────────

const AR_BUCKETS: { label: string; amount: number; color: string }[] = [];
const AR_DELTA_VS_LAST_WEEK = 0; // negative = lower outstanding (good)

function ARAgingSummary() {
  const router = useRouter();
  const total = AR_BUCKETS.reduce((s, b) => s + b.amount, 0);
  // Guard: empty buckets would give Math.max(...[]) = -Infinity.
  const max = AR_BUCKETS.length ? Math.max(...AR_BUCKETS.map((b) => b.amount)) : 0;
  const deltaGood = AR_DELTA_VS_LAST_WEEK < 0;

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-center gap-2 mb-1">
        <Icon name="DocumentCurrencyDollarIcon" size={15} style={{ color: '#F59E0B' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          AR Aging Summary
        </h3>
      </div>
      <p className="text-xs text-gray-400 mb-3">Total outstanding by age bucket</p>
      <div className="space-y-1.5 mb-3">
        {AR_BUCKETS.map((b) => (
          <div key={b.label} className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 w-14">{b.label}</span>
            <div className="flex-1 h-2 rounded-full" style={{ background: '#F3F4F6' }}>
              <div
                className="h-2 rounded-full"
                style={{ width: `${(b.amount / max) * 100}%`, background: b.color }}
              />
            </div>
            <span className="text-[11px] font-semibold text-gray-700 w-14 text-right">
              ${(b.amount / 1000).toFixed(1)}k
            </span>
          </div>
        ))}
      </div>
      <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">Total outstanding</p>
          <p className="text-lg font-bold" style={{ color: '#000150' }}>
            ${(total / 1000).toFixed(1)}k
          </p>
        </div>
        <span className="text-[11px] font-bold" style={{ color: deltaGood ? '#10B981' : '#EF4444' }}>
          {deltaGood ? '▼' : '▲'} ${(Math.abs(AR_DELTA_VS_LAST_WEEK) / 1000).toFixed(1)}k vs last wk
        </span>
      </div>
      <button
        onClick={() => router.push('/financial-accounting-dashboard')}
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 hover:opacity-90 active:scale-95 transition-all"
        style={{ background: 'rgba(7,176,233,0.10)', color: '#0369A1' }}
      >
        Open Financial dashboard
        <Icon name="ArrowRightIcon" size={10} />
      </button>
    </div>
  );
}

// ── 3. Burn vs Revenue ─────────────────────────────────────────────────────

const PNL = {
  revenue:  [] as number[],
  opex:     [] as number[],
  // Last index is "MTD"; (revenue - opex) at that index drives the header.
};

function BurnVsRevenue() {
  const w = 240;
  const h = 100;
  const all = [...PNL.revenue, ...PNL.opex];
  // Guard: empty series would give Math.max(...[]) = -Infinity / Infinity.
  const max = all.length ? Math.max(...all) : 0;
  const min = all.length ? Math.min(...all) * 0.85 : 0;
  const range = max - min || 1;
  const xFor = (i: number) => (i / (PNL.revenue.length - 1)) * w;
  const yFor = (v: number) => h - ((v - min) / range) * (h - 8) - 4;

  const revPath = PNL.revenue.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(v)}`).join(' ');
  const opexPath = PNL.opex.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(v)}`).join(' ');

  // Filled area between revenue (top) and opex (bottom).
  const areaPath = [
    ...PNL.revenue.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(v)}`),
    ...[...PNL.opex].reverse().map((v, idx) => {
      const i = PNL.opex.length - 1 - idx;
      return `L ${xFor(i)} ${yFor(v)}`;
    }),
    'Z',
  ].join(' ');

  // Guard: empty series → no last index; net stays 0 rather than NaN.
  const lastIdx = PNL.revenue.length - 1;
  const net = lastIdx >= 0 ? PNL.revenue[lastIdx] - PNL.opex[lastIdx] : 0;
  const positive = net >= 0;

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-center gap-2 mb-1">
        <Icon name="ChartBarIcon" size={15} style={{ color: '#033494' }} />
        <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          Burn vs Revenue
        </h3>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        MTD net: <span className="font-bold" style={{ color: positive ? '#10B981' : '#EF4444' }}>
          {positive ? '+' : '-'}${Math.abs(net)}k
        </span>
      </p>
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block">
        <path d={areaPath} fill="rgba(16,185,129,0.10)" />
        <path d={revPath}  stroke="#10B981" strokeWidth="1.75" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <path d={opexPath} stroke="#EF4444" strokeWidth="1.5"  fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="flex items-center gap-3 mt-2 text-[11px]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#10B981' }} /> Revenue</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#EF4444' }} /> OpEx</span>
        <span className="ml-auto text-gray-400">Last 6 months</span>
      </div>
    </div>
  );
}
